import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface AnnouncementBannerSettings {
  enabled: boolean;
  content: string;
  bgColor: string;
  textColor: string;
  linkUrl: string | null;
  scrollSpeed: number;
  isLoading: boolean;
}

const DEFAULT_SETTINGS = {
  enabled: false,
  content: "Welcome to PayStreem!",
  bgColor: "256 82% 75%",
  textColor: "0 0% 100%",
  linkUrl: null,
  scrollSpeed: 20,
};

export function useAnnouncementBanner(): AnnouncementBannerSettings {
  const { data, isLoading } = useQuery({
    queryKey: ["announcement-banner-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_text_settings")
        .select("key, value")
        .in("key", [
          "announcement_banner_enabled",
          "announcement_banner_content",
          "announcement_banner_bg_color",
          "announcement_banner_text_color",
          "announcement_banner_link_url",
          "announcement_banner_scroll_speed",
        ]);

      if (error) {
        console.error("Error fetching announcement banner settings:", error);
        return DEFAULT_SETTINGS;
      }

      const settings: Record<string, string | null> = {};
      data?.forEach((item) => {
        settings[item.key] = item.value;
      });

      return {
        enabled: settings.announcement_banner_enabled === "true",
        content:
          settings.announcement_banner_content || DEFAULT_SETTINGS.content,
        bgColor:
          settings.announcement_banner_bg_color || DEFAULT_SETTINGS.bgColor,
        textColor:
          settings.announcement_banner_text_color || DEFAULT_SETTINGS.textColor,
        linkUrl: settings.announcement_banner_link_url || null,
        scrollSpeed:
          parseInt(settings.announcement_banner_scroll_speed || "20") ||
          DEFAULT_SETTINGS.scrollSpeed,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    enabled: data?.enabled ?? DEFAULT_SETTINGS.enabled,
    content: data?.content ?? DEFAULT_SETTINGS.content,
    bgColor: data?.bgColor ?? DEFAULT_SETTINGS.bgColor,
    textColor: data?.textColor ?? DEFAULT_SETTINGS.textColor,
    linkUrl: data?.linkUrl ?? DEFAULT_SETTINGS.linkUrl,
    scrollSpeed: data?.scrollSpeed ?? DEFAULT_SETTINGS.scrollSpeed,
    isLoading,
  };
}
